import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Zap, Dumbbell, Activity, Flame, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { SCHOOL_TIERS } from "@/lib/programs";

const QUALITY_ICONS = {
    speed: Zap,
    strength: Dumbbell,
    elasticity: Activity,
    power: Flame,
    durability: Shield,
};

export default function SchoolTierPage() {
    const { tierSlug } = useParams();
    const tier = SCHOOL_TIERS.find((t) => t.slug === tierSlug);
    if (!tier) return <Navigate to="/programs/school" replace />;

    const others = SCHOOL_TIERS.filter((t) => t.slug !== tier.slug);

    return (
        <div data-testid={`school-tier-page-${tier.slug}`} className="bg-brand-obsidian">
            {/* HERO */}
            <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 border-b border-zinc-900 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.16),transparent_50%),radial-gradient(circle_at_15%_85%,rgba(31,45,85,0.45),transparent_55%)] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <Link
                        to="/programs/school"
                        data-testid="tier-breadcrumb"
                        className="inline-flex items-center gap-2 text-zinc-200 hover:text-white text-[11px] tracking-[0.25em] uppercase mb-10"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Elite Youth Preparation
                    </Link>

                    <div className="grid md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-7">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-5 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" />
                                {tier.eyebrow}
                            </div>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-wider leading-[0.92] text-white">
                                {tier.title.toUpperCase()}.
                            </h1>
                            <div className="mt-6 inline-flex items-baseline gap-3">
                                <div className="font-display text-7xl md:text-8xl text-brand-red tracking-wider leading-none">
                                    {tier.ageRange}
                                </div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">years</div>
                            </div>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-zinc-300 text-base md:text-lg leading-relaxed">
                                {tier.headline}
                            </p>
                            <p className="mt-5 text-zinc-200 leading-relaxed text-sm md:text-base">
                                {tier.summary}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FIVE QUALITIES */}
            <section className="py-20 md:py-28 bg-black border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-7">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                                <span className="inline-block w-8 h-px bg-brand-red" />
                                The five qualities
                            </div>
                            <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white leading-[0.95]">
                                EVERY SESSION
                                <br />HAS A TARGET.
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-zinc-200 leading-relaxed">
                                We coach five athletic qualities — speed, strength, elasticity, power, durability —
                                in stage-correct doses. Here's what this stage emphasizes and why.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900">
                        {tier.qualities.map((q, i) => {
                            const Icon = QUALITY_ICONS[q.key] || Zap;
                            return (
                                <motion.div
                                    key={q.key}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.06 }}
                                    data-testid={`quality-${q.key}`}
                                    className="bg-black p-6 lg:p-8 flex flex-col"
                                >
                                    <Icon className={`w-6 h-6 mb-5 ${i % 2 === 0 ? "text-brand-red" : "text-brand-navy-300"}`} />
                                    <div className="font-display text-2xl tracking-wider text-white leading-tight">
                                        {q.label}
                                    </div>
                                    <p className="mt-4 text-sm text-zinc-200 leading-relaxed flex-1">
                                        {q.detail}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHAT IT LOOKS LIKE */}
            <section className="py-20 md:py-28 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-6">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                            <span className="inline-block w-8 h-px bg-brand-red" />
                            What this stage covers
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                            COACHED.
                            <br />NOT JUST PROGRAMMED.
                        </h2>
                        <ul className="mt-10 space-y-px bg-zinc-900 border border-zinc-900">
                            {tier.bullets.map((b, i) => (
                                <li key={i} className="bg-black px-5 py-4 flex items-start gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-zinc-200 leading-relaxed">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-6">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                            <span className="inline-block w-8 h-px bg-brand-red" />
                            A session, in shape
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                            ONE TARGET.
                            <br />ONE OUTPUT.
                        </h2>
                        <div className="mt-10 border border-zinc-900 bg-zinc-950">
                            {tier.sessionSnapshot.map((row, i) => (
                                <div
                                    key={i}
                                    className="grid grid-cols-12 gap-4 px-5 py-5 border-b border-zinc-900 last:border-b-0"
                                >
                                    <div className="col-span-4 md:col-span-3 text-[10px] tracking-[0.25em] uppercase text-brand-red pt-0.5">
                                        {String(i + 1).padStart(2, "0")} · {row.phase}
                                    </div>
                                    <div className="col-span-8 md:col-span-9 text-sm text-zinc-200 leading-relaxed">
                                        {row.detail}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-xs text-zinc-300 leading-relaxed">
                            Sessions vary by week and block. The shape stays consistent — warm-up, the day's primary quality,
                            strength bias, and a deliberate finisher that protects the next session.
                        </p>
                    </div>
                </div>
            </section>

            {/* OTHER STAGES */}
            <section className="relative py-20 md:py-24 bg-brand-obsidian overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_50%,rgba(31,45,85,0.28),transparent_55%),radial-gradient(circle_at_100%_50%,rgba(230,0,0,0.10),transparent_55%)] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <div className="flex items-end justify-between mb-10">
                        <h2 className="font-display text-3xl md:text-5xl tracking-wider text-white leading-tight">
                            OTHER STAGES
                        </h2>
                        <Link to="/programs/school" className="btn-ghost-tactical hidden md:inline-flex">
                            Stage overview <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                        {others.map((t) => (
                            <Link
                                key={t.slug}
                                to={t.path}
                                data-testid={`other-tier-${t.slug}`}
                                className="group p-8 border border-zinc-900 bg-gradient-to-br from-brand-navy-950 via-zinc-950 to-zinc-950 hover:border-brand-red transition-colors block"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">
                                            {t.eyebrow}
                                        </div>
                                        <div className="mt-2 font-display text-3xl tracking-wider text-white">
                                            {t.title}
                                        </div>
                                        <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-200 mt-2">
                                            Ages {t.ageRange} · {t.focus}
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-zinc-200 group-hover:text-brand-red transition-colors" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
